import { cn } from '../lib/cn';

export interface SkeletonProps {
  className?: string;
  /** Renders as a circle. For avatars. */
  circle?: boolean;
}

/**
 * A loading placeholder.
 *
 * Wrapped in `aria-hidden` because a skeleton has nothing to announce — the region
 * it fills should carry `aria-busy` instead, which `SkeletonTable` and the Table
 * loading state both do.
 */
export function Skeleton({ className, circle }: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'relative block overflow-hidden bg-surface-sunken',
        circle ? 'rounded-full' : 'rounded',
        'after:absolute after:inset-0 after:-translate-x-full after:animate-shimmer',
        'after:bg-gradient-to-r after:from-transparent after:via-black/[0.04] after:to-transparent',
        'dark:after:via-white/[0.06]',
        className,
      )}
    />
  );
}

export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton
          key={index}
          className={cn('h-3.5', index === lines - 1 ? 'w-2/3' : 'w-full')}
        />
      ))}
    </div>
  );
}

export function SkeletonTable({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="flex flex-col divide-y" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading results</span>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-4 px-4 py-3">
          {Array.from({ length: columns }, (_, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className={cn('h-3.5', columnIndex === 0 ? 'w-1/3' : 'flex-1')}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
